import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { 
  Search, 
  Building2, 
  Briefcase, 
  Cpu, 
  TrendingUp,
  Plus,
  Minus
} from "lucide-react";

// Topic Categories with their items
const TOPIC_CATEGORIES = {
  insurance_departments: {
    label: "Insurance Departments",
    icon: Building2,
    color: "blue",
    topics: [
      { id: "underwriting", label: "Underwriting", keywords: ["underwriting", "risk assessment", "policy pricing", "insurance underwriting"] },
      { id: "claims", label: "Claims Management", keywords: ["claims processing", "claims management", "loss adjustment", "claims automation"] },
      { id: "actuarial", label: "Actuarial & Risk", keywords: ["actuarial", "risk modeling", "loss forecasting", "statistical analysis insurance"] },
      { id: "policy-admin", label: "Policy Administration", keywords: ["policy administration", "policy management", "insurance operations", "policy lifecycle"] },
      { id: "compliance", label: "Compliance & Regulatory", keywords: ["insurance compliance", "regulatory", "NAIC", "insurance regulations", "Solvency II"] },
    ]
  },
  business_functions: {
    label: "Business Functions",
    icon: Briefcase,
    color: "emerald",
    topics: [
      { id: "marketing", label: "Marketing & Digital Marketing", keywords: ["insurance marketing", "digital marketing", "customer acquisition", "brand strategy"] },
      { id: "sales", label: "Sales & Distribution", keywords: ["insurance sales", "distribution channels", "agency management", "broker relations"] },
      { id: "finance", label: "Finance & Accounting", keywords: ["insurance finance", "accounting", "financial reporting", "investment management"] },
      { id: "customer-service", label: "Customer Service / CX", keywords: ["customer experience", "customer service", "policyholder experience", "NPS insurance"] },
      { id: "operations", label: "Operations & Process", keywords: ["insurance operations", "process improvement", "operational efficiency", "lean insurance"] },
      { id: "hr", label: "HR & Talent Management", keywords: ["insurance talent", "HR insurance", "workforce management", "employee engagement"] },
    ]
  },
  technology: {
    label: "Technology & Platforms",
    icon: Cpu,
    color: "purple",
    topics: [
      { id: "salesforce", label: "Salesforce / SFMC", keywords: ["Salesforce insurance", "SFMC", "Marketing Cloud", "Sales Cloud insurance", "Salesforce Financial Services"] },
      { id: "crm", label: "CRM Systems", keywords: ["insurance CRM", "customer relationship management", "Guidewire", "Duck Creek"] },
      { id: "insurtech", label: "InsurTech & AI", keywords: ["insurtech", "artificial intelligence insurance", "machine learning insurance", "AI claims"] },
      { id: "analytics", label: "Data Analytics", keywords: ["insurance analytics", "predictive analytics", "data science insurance", "business intelligence"] },
      { id: "automation", label: "Automation & Workflow", keywords: ["insurance automation", "RPA insurance", "workflow automation", "straight-through processing"] },
    ]
  },
  industry_trends: {
    label: "Industry Trends",
    icon: TrendingUp,
    color: "orange",
    topics: [
      { id: "digital-transformation", label: "Digital Transformation", keywords: ["digital transformation insurance", "digitalization", "modernization insurance"] },
      { id: "customer-experience", label: "Customer Experience", keywords: ["CX insurance", "omnichannel", "customer journey", "personalization insurance"] },
      { id: "regulatory-changes", label: "Regulatory Changes", keywords: ["insurance regulation updates", "compliance changes", "new insurance laws"] },
      { id: "market-trends", label: "Market Trends", keywords: ["insurance market trends", "industry outlook", "insurance forecast", "market analysis"] },
      { id: "emerging-risks", label: "Emerging Risks", keywords: ["cyber insurance", "climate risk", "pandemic coverage", "emerging risks insurance"] },
    ]
  }
};

// Get color classes for a category
const getCategoryColors = (category) => {
  const colors = {
    insurance_departments: {
      bg: "bg-blue-50",
      border: "border-blue-200",
      hover: "hover:border-blue-400 hover:bg-blue-100",
      icon: "text-blue-600",
      badge: "bg-blue-100 text-blue-800"
    },
    business_functions: {
      bg: "bg-emerald-50",
      border: "border-emerald-200",
      hover: "hover:border-emerald-400 hover:bg-emerald-100",
      icon: "text-emerald-600",
      badge: "bg-emerald-100 text-emerald-800"
    },
    technology: {
      bg: "bg-purple-50",
      border: "border-purple-200",
      hover: "hover:border-purple-400 hover:bg-purple-100",
      icon: "text-purple-600",
      badge: "bg-purple-100 text-purple-800"
    },
    industry_trends: {
      bg: "bg-orange-50",
      border: "border-orange-200",
      hover: "hover:border-orange-400 hover:bg-orange-100",
      icon: "text-orange-600",
      badge: "bg-orange-100 text-orange-800"
    }
  };
  return colors[category] || colors.insurance_departments;
};

const TIME_PERIODS = [
  { value: 1, label: "1 month" },
  { value: 3, label: "3 months" },
  { value: 6, label: "6 months" },
  { value: 12, label: "1 year" },
  { value: 24, label: "2 years (default)" },
  { value: 60, label: "5 years" },
];

const ResearchPreferences = ({ preferences, onChange }) => {
  const handleTimePeriodChange = (value) => {
    onChange({
      ...preferences,
      time_period_months: parseInt(value)
    });
  };

  const handleTopicToggle = (topicId) => {
    const current = preferences.selected_topics || [];
    const updated = current.includes(topicId)
      ? current.filter(t => t !== topicId)
      : [...current, topicId];
    
    onChange({
      ...preferences,
      selected_topics: updated
    });
  };

  const handleIncludeKeywordsChange = (value) => {
    onChange({
      ...preferences,
      include_keywords: value
    });
  };

  const handleExcludeKeywordsChange = (value) => {
    onChange({
      ...preferences,
      exclude_keywords: value
    });
  };

  const isTopicSelected = (topicId) => {
    return (preferences.selected_topics || []).includes(topicId);
  };

  const getSelectedCount = () => {
    return (preferences.selected_topics || []).length;
  };

  const countKeywords = (text) => {
    if (!text || !text.trim()) return 0;
    return text.split(',').filter(k => k.trim()).length;
  };

  return (
    <Card className="shadow-sm border border-slate-200" data-testid="research-preferences">
      <CardHeader className="bg-gradient-to-r from-slate-50 to-blue-50/50 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Search className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-xl">Research Topics</CardTitle>
          </div>
          <Badge variant="outline" className="text-sm">
            {getSelectedCount()} topics selected
          </Badge>
        </div>
        <CardDescription>Select topics and customize your research sources</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 pt-6">
        {/* Time Period */}
        <div>
          <Label htmlFor="time-period" className="text-sm font-semibold mb-2 block">
            Research Time Period
          </Label>
          <Select
            data-testid="time-period-select"
            value={String(preferences.time_period_months || 24)}
            onValueChange={handleTimePeriodChange}
          >
            <SelectTrigger id="time-period" className="h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_PERIODS.map((period) => (
                <SelectItem key={period.value} value={String(period.value)} data-testid={`period-${period.value}`}>
                  {period.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Topic Categories */}
        {Object.entries(TOPIC_CATEGORIES).map(([categoryKey, category]) => {
          const IconComponent = category.icon;
          const colors = getCategoryColors(categoryKey);
          
          return (
            <div key={categoryKey} className="space-y-3">
              <div className="flex items-center gap-2">
                <IconComponent className={`h-5 w-5 ${colors.icon}`} />
                <Label className="text-sm font-semibold">{category.label}</Label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {category.topics.map((topic) => {
                  const isSelected = isTopicSelected(topic.id);
                  return (
                    <div
                      key={topic.id}
                      className={`flex items-center space-x-3 p-3 rounded-lg border transition-all cursor-pointer ${
                        isSelected 
                          ? `${colors.bg} ${colors.border} border-2` 
                          : `bg-white border-slate-200 ${colors.hover}`
                      }`}
                      onClick={() => handleTopicToggle(topic.id)}
                      data-testid={`topic-${topic.id}`}
                    >
                      <Checkbox
                        id={`topic-${topic.id}`}
                        checked={isSelected}
                        onCheckedChange={() => handleTopicToggle(topic.id)}
                        className="pointer-events-none"
                      />
                      <label htmlFor={`topic-${topic.id}`} className="text-sm cursor-pointer flex-1 font-medium">
                        {topic.label}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Include/Exclude Domains & Keywords Section */}
        <div className="space-y-4 pt-4 border-t border-slate-200">
          <div className="flex items-center gap-2">
            <Search className="h-5 w-5 text-slate-600" />
            <Label className="text-sm font-semibold">Custom Domains & Keywords</Label>
          </div>
          
          {/* Include Box */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="include-keywords" className="text-sm font-medium flex items-center gap-2">
                <Plus className="h-4 w-4 text-emerald-600" />
                Include (prioritize these sources)
              </Label>
              <span className="text-xs text-slate-500">
                {countKeywords(preferences.include_keywords)}/20
              </span>
            </div>
            <Textarea
              id="include-keywords"
              data-testid="include-keywords-input"
              placeholder="e.g., salesforce.com, mckinsey.com, digital transformation, AI automation..."
              value={preferences.include_keywords || ""}
              onChange={(e) => handleIncludeKeywordsChange(e.target.value)}
              className="h-20 resize-none text-sm"
            />
            <p className="text-xs text-slate-500">
              Add domains or keywords to prioritize in research. Separate with commas (max 20).
            </p>
          </div>

          {/* Exclude Box */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="exclude-keywords" className="text-sm font-medium flex items-center gap-2">
                <Minus className="h-4 w-4 text-red-600" />
                Exclude (avoid these sources)
              </Label>
              <span className="text-xs text-slate-500">
                {countKeywords(preferences.exclude_keywords)}/20
              </span>
            </div>
            <Textarea
              id="exclude-keywords"
              data-testid="exclude-keywords-input"
              placeholder="e.g., reddit.com, facebook.com, outdated, legacy systems..."
              value={preferences.exclude_keywords || ""}
              onChange={(e) => handleExcludeKeywordsChange(e.target.value)}
              className="h-20 resize-none text-sm"
            />
            <p className="text-xs text-slate-500">
              Add domains or keywords to exclude from research. Separate with commas (max 20).
            </p>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-900">
            <span className="font-semibold">💡 How it works:</span> Selected topics define your research focus. 
            Use include/exclude to fine-tune sources - add specific domains you trust or keywords to prioritize, 
            and exclude sources or topics you want to avoid.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ResearchPreferences;
